import { useCallback } from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, fetchQuery, useRelayEnvironment } from 'react-relay';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { GenericPickerOption } from '../../common/types.tsx';
import type {
	useSavedFiltersFetchQuery,
	useSavedFiltersFetchQuery$data,
} from './__generated__/useSavedFiltersFetchQuery.graphql';

const UNSUPPORTED_CHARACTERS = /[+\-&|!(){}[\]^~*?\\:]/g;
const removeUnsupportedCharacters = (text: string) =>
	text.replace(UNSUPPORTED_CHARACTERS, ' ').trim();

export const useSavedFiltersFetch = () => {
	const cloudId = useCloudId();
	const environment = useRelayEnvironment();

	return useCallback(
		async (searchString: string) => {
			const sanitizedSearchString = fg('saved_filter_string_sanitize')
				? removeUnsupportedCharacters(searchString)
				: searchString;

			return fetchQuery<useSavedFiltersFetchQuery>(
				environment,
				// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
				graphql`
					query useSavedFiltersFetchQuery($cloudId: ID!, $searchString: String!) {
						jira {
							savedFilters(cloudId: $cloudId, keyword: $searchString, first: 20)
								@required(action: THROW)
								@optIn(to: "JiraFilter") {
								edges {
									node @required(action: THROW) {
										... on JiraCustomFilter {
											filterId
											name
										}
									}
								}
							}
						}
					}
				`,
				{
					cloudId,
					searchString: sanitizedSearchString,
				},
			)
				.toPromise()
				.then((response: useSavedFiltersFetchQuery$data | undefined) => {
					if (response?.jira?.savedFilters?.edges) {
						const data: GenericPickerOption[] =
							response.jira.savedFilters.edges.map((edge) => {
								return {
									label: edge?.node?.name || '',
									value: edge?.node?.filterId || '',
								};
							}) || [];
						return data;
					}

					throw new Error('No response');
				});
		},
		[cloudId, environment],
	);
};
